import { formatSkuPrice } from './marketing/landingPricingSource';
import type { LandingAuthIntent, LandingProductSnapshot } from './landingProductDescriptor.types';
import type { Language } from '@/i18n';

// Localized intent copy is intentionally exhaustive across every supported language and SKU.
// eslint-disable-next-line complexity
export function authDialogMessage(snapshot: Readonly<LandingProductSnapshot>): {
  title: string;
  context?: string;
  closeLabel: string;
} {
  const intent = snapshot.authDialog.intent;
  const price =
    intent && intent !== 'free'
      ? snapshot.story?.catalog.skus.find((sku) => sku.id === intent)
      : undefined;
  const priceLabel = price ? formatSkuPrice(price, snapshot.locale) : null;
  const messages: Record<
    Language,
    Record<LandingAuthIntent | 'generic', { title: string; context?: string }>
  > = {
    fi: {
      generic: {
        title:
          snapshot.authDialog.mode === 'login' ? 'Kirjaudu Lehti AI:hin' : 'Luo Lehti AI -tili',
      },
      free: {
        title: 'Lisää metsäsi maksutta',
        context: 'Luo tili ja jatka suoraan metsän lisäämiseen. Maksukorttia ei tarvita.',
      },
      offer_decode: {
        title: 'Jatka tarjousten vertailuun',
        context: `${priceLabel ?? 'Kertamaksu'} · sisältää vuoden Tonttipaketin. Tilin jälkeen jatkat suoraan omien tarjoustesi tuontiin.`,
      },
      plot_package: {
        title: 'Jatka Tonttipakettiin',
        context: `${priceLabel ?? 'Vuosihinta'}. Tilin jälkeen jatkat valitsemasi metsän Tonttipaketin käyttöönottoon.`,
      },
      buyer_decode: {
        title: 'Jatka ostajan hinta-arvioon',
        context: `${priceLabel ?? 'Kertamaksu'}. Tilin jälkeen jatkat suoraan ostettavan metsän arviointiin.`,
      },
      portfolio_estate: {
        title: 'Jatka metsäomaisuuden hallintaan',
        context: 'Luo tili ja jatka usean metsätilan ratkaisun valintaan.',
      },
    },
    en: {
      generic: {
        title:
          snapshot.authDialog.mode === 'login'
            ? 'Sign in to Lehti AI'
            : 'Create your Lehti AI account',
      },
      free: {
        title: 'Add your forest for free',
        context:
          'Create an account and continue directly to adding your forest. No payment card is required.',
      },
      offer_decode: {
        title: 'Continue to offer comparison',
        context: `${priceLabel ?? 'One-off purchase'} · includes one year of Plot Package. After creating an account, you continue directly to importing your offers.`,
      },
      plot_package: {
        title: 'Continue to Plot Package',
        context: `${priceLabel ?? 'Annual price'}. After creating an account, you continue to activate Plot Package for the selected forest.`,
      },
      buyer_decode: {
        title: 'Continue to the buyer valuation',
        context: `${priceLabel ?? 'One-off purchase'}. After creating an account, you continue directly to valuing the forest you are considering.`,
      },
      portfolio_estate: {
        title: 'Continue to portfolio management',
        context: 'Create an account and continue to the solution for multiple forest properties.',
      },
    },
    sv: {
      generic: {
        title:
          snapshot.authDialog.mode === 'login'
            ? 'Logga in på Lehti AI'
            : 'Skapa ett Lehti AI-konto',
      },
      free: {
        title: 'Lägg till din skog gratis',
        context:
          'Skapa ett konto och fortsätt direkt med att lägga till skogen. Inget betalkort behövs.',
      },
      offer_decode: {
        title: 'Fortsätt till erbjudandejämförelsen',
        context: `${priceLabel ?? 'Engångsköp'} · ett års Skiftpaket ingår. Efter att kontot skapats fortsätter du direkt med att importera erbjudandena.`,
      },
      plot_package: {
        title: 'Fortsätt till Skiftpaketet',
        context: `${priceLabel ?? 'Årspris'}. Efter att kontot skapats fortsätter du med att aktivera paketet för den valda skogen.`,
      },
      buyer_decode: {
        title: 'Fortsätt till köparvärderingen',
        context: `${priceLabel ?? 'Engångsköp'}. Efter att kontot skapats fortsätter du direkt med att värdera skogen du överväger.`,
      },
      portfolio_estate: {
        title: 'Fortsätt till portföljförvaltning',
        context: 'Skapa ett konto och fortsätt till lösningen för flera skogsfastigheter.',
      },
    },
    es: {
      generic: {
        title:
          snapshot.authDialog.mode === 'login'
            ? 'Inicia sesión en Lehti AI'
            : 'Crea tu cuenta de Lehti AI',
      },
      free: {
        title: 'Añade tu bosque gratis',
        context:
          'Crea una cuenta y continúa directamente para añadir tu bosque. No necesitas tarjeta.',
      },
      offer_decode: {
        title: 'Continúa a la comparación de ofertas',
        context: `${priceLabel ?? 'Compra única'} · incluye un año del paquete. Después de crear la cuenta, continuarás directamente para importar tus ofertas.`,
      },
      plot_package: {
        title: 'Continúa al paquete',
        context: `${priceLabel ?? 'Precio anual'}. Después de crear la cuenta, continuarás para activarlo en el bosque seleccionado.`,
      },
      buyer_decode: {
        title: 'Continúa a la valoración para compradores',
        context: `${priceLabel ?? 'Compra única'}. Después de crear la cuenta, continuarás directamente para valorar el bosque que estás considerando.`,
      },
      portfolio_estate: {
        title: 'Continúa a la gestión de cartera',
        context: 'Crea una cuenta y continúa a la solución para varias propiedades forestales.',
      },
    },
  };
  return {
    ...messages[snapshot.locale][intent ?? 'generic'],
    closeLabel: { fi: 'Sulje', en: 'Close', sv: 'Stäng', es: 'Cerrar' }[snapshot.locale],
  };
}
